/**
 * The per-row view model shared by a pipeline's collapsed SUMMARY line and its
 * expanded DETAIL editor.
 *
 * This is the logic that used to live inline in PipelineRow: which catalog
 * models the three dropdowns may offer, which encoded value each control holds,
 * and — the D8 case — whether the LIVE model has fallen off the option list
 * (deprecated after it was set, or a provider/category mismatch). Hoisting it
 * means the collapsed summary can surface a "needs attention" dot without
 * rendering the editor, and the filter bar can ask the same question of every
 * row without duplicating the rules.
 */

import { ModelCatalogRow, PipelineModel, PipelineModelRef } from '../../../services/quillAdminService';
import {
  grokOptionsForPipeline,
  anthropicOptionsForPipeline,
  defaultOptionsForPipeline,
  isAnthropicAllowed,
} from './pipelineCatalog';

export const NONE = '__none__';

export const enc = (provider: string, modelId: string): string => `${provider}::${modelId}`;

export const dec = (value: string): PipelineModelRef => {
  const idx = value.indexOf('::');
  return { provider: value.slice(0, idx), model_id: value.slice(idx + 2) };
};

export interface PipelineRowModel {
  isFixed: boolean;
  isSafety: boolean;
  anthropicAllowed: boolean;

  grokOptions: ModelCatalogRow[];
  anthropicOptions: ModelCatalogRow[];
  defaultOptions: ModelCatalogRow[];

  active: PipelineModelRef;
  dflt: PipelineModelRef;

  /** Encoded control values (NONE / '' for "nothing selected"). */
  grokValue: string;
  anthropicValue: string;
  defaultValue: string;

  /** The live pointer is not in the corresponding option list (D8). */
  grokMissing: boolean;
  anthropicMissing: boolean;
  defaultMissing: boolean;

  /** Closed-control markers: '(deprecated)' / '(unavailable)', or undefined. */
  grokMarker?: string;
  anthropicMarker?: string;
  defaultMarker?: string;

  /** Long notes for the synthetic keep-this-model menu entries. */
  activeNote: string;
  defaultNote: string;

  /** The live active/default model is a catalog row xAI has retired. */
  activeDeprecated: boolean;
  defaultDeprecated: boolean;

  /**
   * Anything an operator must look at: an off-catalog live pointer or a
   * deprecated one. Drives the summary-line warning dot and the
   * "Needs attention" filter, so a problem is never hidden by a collapsed row.
   */
  hasProblem: boolean;
  /** Human sentence for the warning dot's tooltip ('' when hasProblem false). */
  problemNote: string;
}

/** Builds the shared view model for one pipeline against the current catalog. */
export function buildPipelineRowModel(pipeline: PipelineModel, catalog: ModelCatalogRow[]): PipelineRowModel {
  const { category, pipeline_key } = pipeline;
  const isFixed = category === 'fixed';
  const isSafety = pipeline.is_safety_path === true;
  const anthropicAllowed = isAnthropicAllowed(category);

  const grokOptions = grokOptionsForPipeline(catalog, category, pipeline_key);
  const anthropicOptions = anthropicOptionsForPipeline(catalog, category, pipeline_key);
  const defaultOptions = defaultOptionsForPipeline(catalog, category, pipeline_key);

  const active = pipeline.active;
  const dflt = pipeline.default;

  const grokValue = active.provider === 'xai' && active.model_id ? enc('xai', active.model_id) : NONE;
  const anthropicValue =
    active.provider === 'anthropic' && active.model_id ? enc('anthropic', active.model_id) : NONE;
  const defaultValue = dflt.model_id ? enc(dflt.provider, dflt.model_id) : '';

  const grokOptionValues = new Set(grokOptions.map((m) => enc('xai', m.model_id)));
  const anthropicOptionValues = new Set(anthropicOptions.map((m) => enc('anthropic', m.model_id)));
  const defaultOptionValues = new Set(defaultOptions.map((m) => enc(m.provider ?? 'xai', m.model_id)));

  const grokMissing = !isFixed && grokValue !== NONE && !grokOptionValues.has(grokValue);
  const anthropicMissing = !isFixed && anthropicValue !== NONE && !anthropicOptionValues.has(anthropicValue);
  const defaultMissing = !isFixed && defaultValue !== '' && !defaultOptionValues.has(defaultValue);

  // Two distinct reasons a live model is off-catalog, and the operator needs to
  // tell them apart: "deprecated" (the row exists, the provider retired it) is a
  // scheduled migration; "unavailable" (no row at all — e.g. prosody_emotional's
  // grok-4-1-fast-non-reasoning before migration 544) means the catalog is
  // missing data and any re-selection would fail MODEL_NOT_IN_CATALOG.
  const isDeprecated = (ref: PipelineModelRef): boolean =>
    catalog.some(
      (r) => r.model_id === ref.model_id && (r.provider ?? 'xai') === (ref.provider || 'xai') && !!r.deprecated_at
    );

  const activeDeprecated = isDeprecated(active);
  const defaultDeprecated = isDeprecated(dflt);

  const longNote = (deprecated: boolean): string =>
    deprecated ? 'deprecated — still active' : 'unavailable — still active';
  const shortMarker = (deprecated: boolean): string => (deprecated ? '(deprecated)' : '(unavailable)');

  const hasProblem =
    grokMissing || anthropicMissing || defaultMissing || activeDeprecated || defaultDeprecated;

  let problemNote = '';
  if (hasProblem) {
    if (grokMissing || anthropicMissing) {
      problemNote = activeDeprecated
        ? 'The live active model is deprecated — pick a current model.'
        : 'The live active model is not in the catalog — re-selecting it would fail.';
    } else if (defaultMissing) {
      problemNote = defaultDeprecated
        ? 'The default model is deprecated — pick a current default.'
        : 'The default model is not in the catalog.';
    } else {
      problemNote = 'This pipeline points at a deprecated model.';
    }
  }

  return {
    isFixed,
    isSafety,
    anthropicAllowed,
    grokOptions,
    anthropicOptions,
    defaultOptions,
    active,
    dflt,
    grokValue,
    anthropicValue,
    defaultValue,
    grokMissing,
    anthropicMissing,
    defaultMissing,
    grokMarker: grokMissing ? shortMarker(activeDeprecated) : undefined,
    anthropicMarker: anthropicMissing ? shortMarker(activeDeprecated) : undefined,
    defaultMarker: defaultMissing ? shortMarker(defaultDeprecated) : undefined,
    activeNote: longNote(activeDeprecated),
    defaultNote: longNote(defaultDeprecated),
    activeDeprecated,
    defaultDeprecated,
    hasProblem,
    problemNote,
  };
}

/** True when a pipeline would show the summary-line warning dot. */
export function pipelineNeedsAttention(pipeline: PipelineModel, catalog: ModelCatalogRow[]): boolean {
  return buildPipelineRowModel(pipeline, catalog).hasProblem;
}

/** Case-insensitive substring match against display_name / pipeline_key. */
export function pipelineMatchesSearch(pipeline: PipelineModel, search: string): boolean {
  const q = search.trim().toLowerCase();
  if (!q) return true;
  return (
    pipeline.display_name.toLowerCase().includes(q) || pipeline.pipeline_key.toLowerCase().includes(q)
  );
}
